<template>
  <div class="left">
    <StepperCard
      path="/cart"
      title="Choose Your Dumpster"
      :isFutureStep="false"
      :isDumpsterStepper="true"
    />
    <StepperCard
      path="/cart/service"
      title="Service Info &amp; Placement"
      :isFutureStep="false"
    />
    <h2 class="header">
      <span class="header__number">3</span>Payment Information
    </h2>
    <p class="disclaimer disclaimer__italic">* indicates a required field</p>
    <form id="paymentForm" class="payment-form" @submit.prevent="submitPaymentEvents">
      <div class="ecom checkbox">
        <label class="ecom checkbox__label">
          <div v-if="!billingSameAsDelivery" class="ecom checkbox__fauxbox"></div>
          <img class="ecom checkbox__check-img" v-if="billingSameAsDelivery" :src="activeCheckbox">
          <input
            v-model="billingSameAsDelivery"
            @change="updateBillingFields"
            class="ecom checkbox__input"
            type="checkbox"
          />
          <span class="ecom checkbox__text">
            Check if Billing Information is the same as Service &amp; Contact Info
          </span>
        </label>
      </div>
      <fieldset v-show="!billingSameAsDelivery" class="fieldset mt-2" ref="billingFields">
        <legend class="legend">Billing Information</legend>
        <label class="label">
          First Name *
          <input
            v-model="billingFirstName"
            :required="!billingSameAsDelivery"
            class="input"
            placeholder="Enter Billing First Name*"
            type="text"
          />
        </label>
        <label class="label">
          Last Name *
          <input
            v-model="billingLastName"
            :required="!billingSameAsDelivery"
            class="input"
            onchange="this.setCustomValidity('')"
            oninvalid="this.setCustomValidity('Last name required. Digits not allowed.')"
            pattern="[^0-9]+"
            placeholder="Enter Billing Last Name*"
            type="text"
          />
        </label>
        <label class="label">
          Email *
          <input
            v-model="billingEmailAddress"
            :required="!billingSameAsDelivery"
            class="input"
            onchange="this.setCustomValidity('')"
            oninvalid="this.setCustomValidity('This is not a valid email address.')"
            pattern="[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}"
            placeholder="Enter Billing Email *"
            type="email"
          />
        </label>
        <label class="label">
          Phone Number *
          <small class="payment-form__helper">Provide the number of the best person to contact for questions regarding this order</small>
          <input
            v-model="billingPhone"
            v-maska="phoneMask"
            :required="!billingSameAsDelivery"
            class="input"
            onchange="this.setCustomValidity('')"
            oninvalid="this.setCustomValidity('Please enter a valid phone number.')"
            placeholder="(999) 999-9999 *"
            ref="billingPhone"
            type="tel"
          />
        </label>
        <label class="label">
          Address*
          <input
            v-model="billingStreetAddress"
            @input="addressInput"
            :required="!billingSameAsDelivery"
            class="input"
            placeholder="e.g. 123 Main Street"
            type="text"
          />
        </label>
        <label class="label">
          Address 2 (optional)
          <input
            v-model="billingAddress2"
            @input="onBillingFieldChange"
            class="input"
            placeholder="Apt, Suite, etc. (optional)"
            type="text"
          />
        </label>
        <label class="label">
          City *
          <input
            v-model="billingCity"
            :required="!billingSameAsDelivery"
            class="input"
            placeholder="Enter City*"
            type="text"
            @input="onBillingFieldChange"
          />
        </label>
        <label class="label">
          State *
          <StateDropdown
            v-model="billingState"
            :options="statesList"
            :placeholder="'Select State*'"
            :required="!billingSameAsDelivery"
            :showValue="true"
            :errorMessage="'Please enter a valid state.'"
            :maxLength="2"
          />
        </label>
        <label class="label">
          ZIP Code *
          <input
            v-model="billingZip"
            :required="!billingSameAsDelivery"
            class="input"
            placeholder="Enter ZIP Code*"
            type="text"
            inputmode="numeric"
            maxlength="5"
            pattern="[0-9]{5}"
            onchange="this.setCustomValidity('')"
            oninvalid="this.setCustomValidity('Please enter a valid ZIP code.')"
            @input="formatZipInput"
          />
        </label>
      </fieldset>
      <h2 class="legend mt-2">Customer Service Agreement</h2>
      <div class="infobox">
        <p class="mb-1">By reading and accepting {{companyNameCSA}} Customer Service Agreement, you are confirming that you understand and agree to comply with the following delivery, pricing, scheduling and dumpster weight guidelines.</p>
        <p class="mb-1">We will not automatically come to pick up the dumpster rental at any point. You must schedule your dumpster pickup by no later than the last date of your rental period, which is {{endOfRentalPeriodDate}}. You can schedule your dumpster pickup by visiting the {{companyName}} website and <a target="_blank" rel="noopener" :href="scheduleAPickup">filling out this form</a>. If you keep the dumpster past the allotted rental period and have not scheduled a pickup by the end of this rental period, you will be charged an extension fee of ${{rentalPeriodOverage}} for each additional day.</p>
        <p class="mb-1">This price includes the first {{tonnage}} ton(s) of your disposal. If you exceed this weight limit, you will be subject to a ${{tonnageOverage}} per-ton prorated fee which will be assessed at the landfill. Our trucks cannot haul more than 10 tons total.</p>
        <p class="mb-1">Please do not use the phone number printed on your dumpster for any service questions or pickup requests. Instead, contact {{companyName}} directly at <a :href="`tel:+${phoneNumber}`">{{phoneNumber}}</a> for any related queries or needs.</p>
        <p class="mb-1">When you are filling your dumpster, please load it as evenly as you can to prevent overloading. This will also help you maximize the amount of space inside for your items. Additionally, keep the debris inside the container level and make sure that no items are sticking above the maximum load line. Overloading the container may result in a $195 trip fee if our driver cannot pick up your dumpster.</p>
        <p class="mb-1">There are a few items that we cannot accept in the dumpster. These include tires, batteries and railroad ties. We also cannot accept anything flammable, toxic or hazardous. Finally, we cannot accept any paints, oils, asbestos, refrigerators, air conditioners, food waste or 50-gallon drums. For more detailed information about <a target="_blank" rel="noopener" :href="prohibitedItems">prohibited items</a>, please review your invoice or contact us with questions about your debris types.</p>
        <p class="mb-1"><strong>If you dispose of a restricted or prohibited item in your dumpster, you may need to pay an additional disposal fee. You can avoid these fees by following your area disposal regulations and the guidelines on your invoice.</strong></p>
        <p class="mb-1">Our delivery window runs from 6 a.m. – 6 p.m. on the day of delivery. If we cannot deliver the container for any reason outside of the driver’s control, there will be a $195 trip fee. Lastly, if you need to cancel the delivery, you have until 3 p.m. the day before for a $25 cancellation fee. If you cancel or make changes after 3 p.m. the day before the delivery, or on the day of delivery, there will be a $195 fee.</p>
        <p>Terms and conditions are subject to change, and the final details of your purchase will be emailed to you with your order confirmation.</p>
      </div>
      <div class="checkbox mt-2">
        <label class="checkbox__label checkbox__label-top-align">
          <div v-if="!billingCustomerServiceAgreement" class="checkbox__fauxbox"></div>
          <img class="checkbox__check-img" v-if="billingCustomerServiceAgreement" :src="activeCheckbox">
          <input
            @change="csaToggle"
            v-model="billingCustomerServiceAgreement"
            class="checkbox__input"
            type="checkbox"
            required
          />
          <span class="checkbox__text">
            <strong>I have read and agree to the Customer Service Agreement.</strong> In doing so I acknowledge and accept the following:
            <ul class="checkbox__list">
              <li>Exceeding my rental period will result in an additional charge of <strong>${{rentalPeriodOverage}}</strong> per day.</li>
              <li>Exceeding the allotted debris tonnage included in your bundled rate will result in a prorated charge of up to <strong>${{tonnageOverage}}</strong> per additional ton.</li>
              <li>Throwing away restricted items in my dumpster may result in an additional charge.</li>
              <li>I have read and accept the <a :href="serviceAgreementLink" target="_blank">Service Agreement Terms &amp; Conditions</a></li>
            </ul>
          </span>
        </label>
      </div>
      <fieldset class="fieldset mt-2">
        <legend class="legend">Service Notifications</legend>
        <p>Enter your cell phone number and subscribe to SMS updates about your service. (Optional)</p>
        <label class="label">
          <input
            v-model="billingMobilePhoneNumber"
            v-maska="phoneMask"
            class="input"
            placeholder="Mobile Phone Number (optional)"
            ref="servicePhoneNumber"
            type="tel"
          />
        </label>
      </fieldset>
      <div class="checkbox">
        <label class="checkbox__label">
          <div v-if="!consentToReceiveTexts" class="checkbox__fauxbox"></div>
          <img class="checkbox__check-img" v-if="consentToReceiveTexts" :src="activeCheckbox">
          <input
            v-model="consentToReceiveTexts"
            class="checkbox__input"
            type="checkbox"
          />
          <span class="checkbox__text">
            I consent to receive informational and marketing text messages from and on behalf of {{companyName}} at the phone number I provided above. Message &amp; Data Rates may apply. Message frequency may vary.
          </span>
        </label>
      </div>
      <fieldset class="fieldset mt-2">
        <legend class="legend">Add a Promotional Code</legend>
        <label class="label">
          <input
            @keydown.enter.prevent="validateDiscount"
            v-model="discountCode"
            class="input"
            placeholder="Enter Code"
            type="text"
          />
        </label>
      </fieldset>
      <div class="input__accessories">
        <div class="input__message" v-if="discountCodeValid !== null">
          <img class="input__status-icon" :src="discountCodeValid ? successIcon : declinedIcon"/>
          <span v-if="discountCodeValid">Your promotional discount has been successfully applied.</span>
          <span v-if="!discountCodeValid">The promotional code you entered is not valid.</span>
        </div>
        <button
          @click="validateDiscount"
          class="btn btn--secondary input__btn"
          type="button"
        >
          Apply
        </button>
      </div>
      <fieldset class="fieldset mt-2">
        <Lightbox
          v-if="mountLightbox"
          @submit-events="submitPaymentEvents"
          @lightbox-approval="onApproval"
          @lightbox-cancelled="onCancelled"
          @lightbox-error="onError"
          @lightbox-declined="onDeclined"
          :firstName="billingFirstName"
          :lastName="billingLastName"
          :address="`${billingAddress} ${billingStreet}, ${billingCity}, ${billingState} ${billingZip}`"
          :zip="billingZip"
          :billingAddress2="billingAddress2"
          :billingCustomerServiceAgreement="billingCustomerServiceAgreement"
          :formCriteriaMet="formCriteriaMet"
          :onLightboxInitiated="onLightboxInitiated"
        />
      </fieldset>

      <div class="payment-form__wastepay-badge">
        <img class="payment-form__creditcards" :src="wastePayBadge">
      </div>
    </form>
  </div>

  <!-- Payment Processing Modal -->
  <modal :isOpen=paymentIsProcessing :showClose="false">
    <template v-slot:content>
      <img class="theme-logo" :src="themeLogo" />
      <img class="spin" :src="loadingSpinner" />
      <h3 class="legend">Processing your payment</h3>
      <p>This may take a few moments. Please do not click back, close or refresh your browser while we process your information.</p>
    </template>
  </modal>

   <!-- Salesforce Error Modal -->
    <modal class="salesforce-modal-error" :isOpen=showErrorModal :showClose="false">
      <template v-slot:content>
        <div>
          <img class="salesforce-modal-error__img" :src="themeLogo" />
          <h3 class="salesforce-modal-error__heading">We're Sorry, But Something Went Wrong.</h3>
          <p class="mb-1" >Unfortunately, our servers have encountered an error and we were unable to process your order.</p>
          <p class="mb-1" ><strong>Your order information was not saved into our system. Please call our team of dumpster rental experts during business hours to finalize your order.</strong> </p>
          <div><a class="salesforce-modal-error__phone" :href="`tel:+${phoneNumber}`">{{phoneNumber}}</a></div>
        </div>
      </template>
    </modal>
</template>
<script>

import { mapState, mapActions, mapGetters } from 'vuex'
import { addDays, lightFormat } from 'date-fns'
import * as Sentry from '@sentry/vue'
import StepperCard from '@/components/StepperCard.vue'
import Modal from '@/components/Modal.vue'
import Lightbox from '@/components/converge/Lightbox.vue'
import config from '@/config'

import {
  submitPaymentEvents,
  paymentPageViewEvents,
  getLast4CC
} from '@/utilities/payment'

import { completeCheckout, generateIdempotencyKey, shouldRotateIdempotencyKey } from '@/utilities/checkout'
import {
  getNumericValue,
  getFormattedPhoneNumber,
  getAbandonedDNIS,
  getDNIS,
  getCCType,
  generateReCaptchaToken
} from '@/utilities.js'
import { states } from '../utilities/constants'
import StateDropdown from '../components/StateDropdown.vue'
const { cdn, companyName, companyNameCSA, phoneNumber, links: { scheduleAPickup, prohibitedItems, serviceAgreementLink } } = config

// TODO:
// Mounted function cleanup/WTF
// Move email call to backend

export default {
  components: {
    StepperCard,
    Modal,
    Lightbox,
    StateDropdown
  },
  beforeRouteEnter (to, from, next) {
    if (to.name === 'Payment' && from.name !== 'Service') {
      next('/')
    } else {
      next()
    }
  },
  data () {
    return {
      showAddressError: false,
      mountLightbox: true,
      brand: process.env.VUE_APP_BRAND,
      creditCards: `${cdn}credit-cards-logos.svg`,
      wastePayBadge: `${cdn}wastepay-secure-payments-badge.svg`,
      tooltip: `${cdn}tooltip.svg`,
      iconPaymentLock: `${cdn}icons-payment-lock.svg`,
      activeCheckbox: `${cdn}icons-active-checkbox.svg`,
      loadingSpinner: `${cdn}icons-spinner-loading.svg`,
      themeLogo: `${cdn}theme-logo.png`,
      successIcon: `${cdn}success-icon.svg`,
      declinedIcon: `${cdn}declined-icon.svg`,
      resubmitIcon: `${cdn}resubmit-icon.svg`,
      convergeEmbeddedPayment: null,
      paymentIsProcessing: false,
      dismissableErrorMessage: '',
      companyName,
      companyNameCSA,
      phoneNumber,
      scheduleAPickup,
      serviceAgreementLink,
      prohibitedItems,
      showErrorModal: false,
      dateMask: '##/##',
      phoneMask: ['(###) ###-####', '# (###) ###-####'],
      creditCardMask: '#### #### #### ####',
      transactionId: null,
      checkoutIdempotencyKey: null,
      convergeResponse: {},
      last4CC: '',
      timeout: null,
      lightboxInitiated: false,
      states,
      statesList: Object.entries(states).map(([value, label]) => ({
        label,
        value
      }))
    }
  },
  watch: {
    billingSameAsDelivery () {
      this.resetLightbox()
    },
    consentToReceiveTexts (newValue) {
      if (newValue === false) this.resetLightbox()
    },
    billingCustomerServiceAgreement (newValue) {
      if (newValue === false) this.resetLightbox()
    }
  },
  async mounted () {
    const { discountCode, discountCodeValid } = this.$store.state.cart
    const hasCartItem = !!this.$store.state.cart.item
    if (discountCode && hasCartItem && discountCodeValid !== true) {
      await this.getDiscount({ useCartItem: true })
    } else if (!discountCode) {
      this.$store.commit('cart/discountCodeValid', null)
    }
    const { utmSource, attribution } = this.$store.state.customer
    const DNIS = getAbandonedDNIS(utmSource, attribution)
    this.$store.commit('customer/DNIS', DNIS)
    const UUID = this.ppcLeadUUID
    const abandonedPayloadAdditions = {
      abandonedCartFlag: true,
      DNIS
    }

    // if this is not from a PPC landing page, an abandoned lead has not been sent yet, the session timeout modal is not open and not on the confirmation page, create an abandoned lead if the customer attempts to exit the experience
    window.addEventListener('beforeunload', (event) => {
      if (!UUID && !this.abandonedLeadSent && !document.querySelector('.session-timeout-modal') && this.$route.path !== '/cart/confirmation') {
        this.createLead(abandonedPayloadAdditions)
        event.preventDefault()
        event.returnValue = ''
      }
    })

    // TODO: This timeout needs cancelled if user converts
    // if this is a PPC lead and the user has not converted in 30 mins, update the PPC lead to abandoned OR create a non PPC abandoned lead
    this.timeout = setTimeout(() => {
      if (UUID) {
        this.updateLead(abandonedPayloadAdditions)
      } else if (!this.abandonedLeadSent) { // we only want to create an abandoned lead if a lead hasn't already been created
        this.createLead(abandonedPayloadAdditions)
      }
    }, 1800000) // 30 mins

    // TODO: Clean this up
    const phoneInput = this.$refs.billingPhone
    const servicePhoneNumber = this.$refs.servicePhoneNumber
    phoneInput.addEventListener('keyup', (e) => {
      const firstChar = parseInt(e.target.value.replace(/[^\d.-]+/g, '').charAt(0))
      const mask = firstChar === 1 ? '1 (###) ###-####' : '(###) ###-####'
      e.target.setAttribute('data-mask', mask)
    })
    servicePhoneNumber.addEventListener('keyup', (e) => {
      const firstChar = parseInt(e.target.value.replace(/[^\d.-]+/g, '').charAt(0))
      const mask = firstChar === 1 ? '1 (###) ###-####' : '(###) ###-####'
      e.target.setAttribute('data-mask', mask)
    })

    paymentPageViewEvents()
    this.checkoutIdempotencyKey = sessionStorage.getItem('checkoutIdempotencyKey') || generateIdempotencyKey()
    sessionStorage.setItem('checkoutIdempotencyKey', this.checkoutIdempotencyKey)
    // Autofill billing phone from customer phone
    if (this.$store.state.customer.phoneNumber) {
      this.$store.commit('customer/billingMobilePhoneNumber', this.$store.state.customer.phoneNumber)
    }
  },
  methods: {
    ...mapActions('cart', [
      'createLead',
      'getDiscount',
      'updateLead'
    ]),
    // Validate discount against cart item (calls getDiscount with useCartItem: true)
    validateDiscount () {
      return this.getDiscount({ useCartItem: true })
    },
    csaToggle (e) {
      // Accepted terms
      if (e.target.checked) {
        this.$store.state.customer.acceptedCSATimestamp = new Date().toISOString()
        return
      }
      // Rejected terms
      this.$store.state.customer.acceptedCSATimestamp = null
    },
    resetLightbox () {
      this.lightboxInitiated = false
      // Re-init Lightbox
      this.mountLightbox = false
      this.$nextTick(() => {
        this.mountLightbox = true
      })
    },
    addressInput () {
      this.updateIsBillingAddressValid(false)
      this.onBillingFieldChange()
    },
    onLightboxInitiated () {
      this.lightboxInitiated = true
    },
    // onCancelled is triggered by lightbox after 15 minutes
    onCancelled (data) {
      // Remounting the component will re-initialize lightbox with fresh tokens
      this.mountLightbox = false
      this.$nextTick(() => {
        this.mountLightbox = true
      })
    },
    async onApproval (data) {
      this.paymentIsProcessing = true
      clearTimeout(this.timeout)
      this.convergeResponse = data.convergeResponse
      try {
        await this.processCheckoutV2(data)
      } catch (e) {
        Sentry.withScope(scope => {
          scope.setTag('checkout', 'v2_checkout')
          scope.setLevel('fatal')
          Sentry.captureException(e)
        })
        if (shouldRotateIdempotencyKey(e)) {
          const newKey = generateIdempotencyKey()
          this.checkoutIdempotencyKey = newKey
          sessionStorage.setItem('checkoutIdempotencyKey', newKey)
          sessionStorage.removeItem('checkoutCorrelationId')
        }
        this.paymentIsProcessing = false
        this.showErrorModal = true
      }
    },
    async processCheckoutV2 (data) {
      const { ssl_token: sslToken, ssl_exp_date: sslExpDate, ssl_card_number: sslCardNumber } = this.convergeResponse
      const DNIS = getDNIS(this.$store.state.customer.utmSource, this.$store.state.customer.attribution)
      const deliveryDate = `${this.deliveryDate.getMonth() + 1}/${this.deliveryDate.getDate()}/${this.deliveryDate.getFullYear()}`

      this.$store.commit('customer/deliveryDate', new Date(this.deliveryDate.setHours(0, 0, 0, 0)))
      this.$store.commit('customer/billingExpirationDate', sslExpDate)
      this.$store.commit('customer/deliveryDateMilliseconds', new Date(deliveryDate).getTime())
      this.$store.commit('customer/DNIS', DNIS)

      const billingAddress = this.billingStreetAddress ||
        `${this.$store.state.customer.serviceAddress.streetAddress} ${this.$store.state.customer.serviceAddress.streetName}` || ''

      const cardLastFour = sslCardNumber ? sslCardNumber.slice(-4) : ''
      const ccType = getCCType(this.convergeResponse.ssl_card_short_description || '')
      const reCaptchaToken = await generateReCaptchaToken('TRANSACTION').catch((error) => {
        console.error('Failed to generate reCAPTCHA token', error)
        return null
      })
      const response = await completeCheckout({
        idempotencyKey: this.checkoutIdempotencyKey,
        authToken: data.authToken,
        sslToken,
        amount: this.totalAmount,
        taxAmount: this.taxAmount,
        ccType,
        reCaptchaToken,
        customer: this.$store.state.customer,
        billing: {
          email: this.billingEmailAddress,
          firstName: this.billingFirstName,
          lastName: this.billingLastName,
          address: billingAddress,
          apartmentNumber: this.billingAddress2,
          city: this.billingCity,
          state: this.billingState,
          zip: this.billingZip,
          expiration: sslExpDate,
          phoneNumber: this.billingPhone,
          country: this.billingCountry
        },
        shippingAddress: {
          address: this.serviceAddress.streetAddress,
          city: this.serviceAddress.city,
          state: this.serviceAddress.state,
          zip: this.serviceAddress.zip,
          country: this.serviceAddress.country
        },
        cardLastFour,
        payment: {
          sslToken,
          last4CC: cardLastFour
        },
        cart: this.$store.state.cart,
        project: this.$store.state.project,
        onPoint: this.$store.state.onPoint,
        dumpster: this.$store.state.dumpster,
        ppcLeadUUID: this.ppcLeadUUID,
        savedCartUUID: this.$store.state.cart.savedCartUUID,
        utmSource: this.$store.state.cart.savedCartUtmSource,
        utmMedium: this.$store.state.cart.savedCartUtmMedium,
        utmCampaign: this.$store.state.cart.savedCartUtmCampaign,
        savedCartCreatedAt: this.$store.state.cart.savedCartCreatedAt,
        sfExtras: { brand: this.brand }
      })

      this.transactionId = response.data.orderId
      this.last4CC = sslCardNumber ? getLast4CC(sslCardNumber) : ''
      if (this.ppcLeadUUID) {
        this.$store.commit('cart/abandonedLeadSent', true)
      }
      sessionStorage.removeItem('checkoutIdempotencyKey')
      sessionStorage.removeItem('checkoutCorrelationId')
      this.paymentIsProcessing = false
      this.$router.push('/cart/confirmation')
    },
    onError () {
      console.log('onError')
      this.showErrorModal = true
      this.paymentIsProcessing = false
    },
    // Fired when user selects cancel after declined card
    onDeclined (response) {
      // Remounting the component will re-initialize lightbox with fresh tokens
      this.mountLightbox = false
      this.$nextTick(() => {
        this.mountLightbox = true
      })
    },
    submitPaymentEvents () {
      submitPaymentEvents()
      return null
    },
    updateIsBillingAddressValid (bool) {
      this.$store.commit('customer/isBillingAddressValid', bool)
    },
    updateBillingFields (event) {
      // This method will reset the custom validity of all billing fields when "billing same as service" is checked.
      // Without this, if any were previously marked invalid they would be stuck that way after the fieldset is hidden, preventing checkout.
      if (event.target.checked) {
        const billingFieldset = this.$refs.billingFields
        Array.from(billingFieldset.elements).forEach(input => input.setCustomValidity(''))
      }
    },
    splitAndSetStreetAddress (value) {
      let streetNumber = ''
      let streetName = ''
      if (typeof value === 'string' && value.trim()) {
        const [first, ...rest] = value.trim().split(/\s+/)
        streetNumber = first
        streetName = rest.join(' ')
      }
      this.$store.commit('customer/billingAddress', streetNumber)
      this.$store.commit('customer/billingStreet', streetName)
    },
    onBillingFieldChange () {
      if (this.lightboxInitiated) {
        this.resetLightbox()
      }
    },
    formatZipInput (event) {
      const value = event.target.value.replace(/[^0-9]/g, '').slice(0, 5)
      this.billingZip = value
      this.onBillingFieldChange()
    }
  },
  computed: {
    ...mapState('cart', [
      'abandonedLeadSent',
      'discountAmount',
      'discountCodeValid',
      'item'
    ]),
    ...mapState('customer', [
      'isServiceAddressValid',
      'isBillingAddressValid'
    ]),
    ...mapGetters('cart', ['totalAmount', 'taxAmount', 'fuelSurcharge', 'cartDiscountCode']),
    formCriteriaMet () {
      return this.billingZip &&
        this.billingEmailAddress &&
        this.billingPhone &&
        this.billingStreetAddress &&
        this.billingCity &&
        this.billingState &&
        this.billingZip &&
        this.billingCustomerServiceAgreement &&
        this.billingFirstName &&
        this.billingLastName &&
        !this.showErrorModal &&
        this.isBillingAddressValid
    },
    billingFirstName: {
      get () {
        return this.$store.state.customer.billing.billingFirstName
      },
      set (value) {
        this.$store.commit('customer/billingFirstName', value)
        this.onBillingFieldChange()
      }
    },
    billingLastName: {
      get () {
        return this.$store.state.customer.billing.billingLastName
      },
      set (value) {
        this.$store.commit('customer/billingLastName', value)
        this.onBillingFieldChange()
      }
    },
    billingEmailAddress: {
      get () {
        return this.$store.state.customer.billing.billingEmailAddress
      },
      set (value) {
        this.$store.commit('customer/billingEmailAddress', value)
        this.onBillingFieldChange()
      }
    },
    billingPhone: {
      get () {
        return this.$store.state.customer.billing.billingPhone
      },
      set (value) {
        this.$store.commit('customer/billingPhone', getFormattedPhoneNumber(value))
        this.onBillingFieldChange()
      }
    },
    billingStreetAddress: {
      get () {
        return this.$store.state.customer.billing.billingAddress.billingStreetAddress
      },
      set (value) {
        this.$store.commit('customer/billingStreetAddress', value)
        this.splitAndSetStreetAddress(value)
        this.onBillingFieldChange()
      }
    },
    billingAddress2: {
      get () {
        return this.$store.state.customer.billing.billingAddress.billingAddress2
      },
      set (value) {
        this.$store.commit('customer/billingAddress2', value)
        this.onBillingFieldChange()
      }
    },
    serviceAddress2: {
      get () {
        return this.$store.state.customer.serviceAddress.serviceAddress2
      }
    },
    billingAddress: {
      get () {
        return this.$store.state.customer.billing.billingAddress.address
      },
      set (value) {
        this.$store.commit('customer/billingAddress', value)
      }
    },
    billingStreet: {
      get () {
        return this.$store.state.customer.billing.billingAddress.street
      },
      set (value) {
        this.$store.commit('customer/billingStreet', value)
      }
    },
    billingCity: {
      get () {
        return this.$store.state.customer.billing.billingAddress.city
      },
      set (value) {
        this.$store.commit('customer/billingCity', value)
        this.onBillingFieldChange()
      }
    },
    billingState: {
      get () {
        return this.$store.state.customer.billing.billingAddress.state
      },
      set (value) {
        this.$store.commit('customer/billingState', value)
        this.onBillingFieldChange()
      }
    },
    billingZip: {
      get () {
        return this.$store.state.customer.billing.billingAddress.zip
      },
      set (value) {
        this.$store.commit('customer/billingZip', value)
        this.onBillingFieldChange()
      }
    },
    billingCountry: {
      get () {
        return this.$store.state.customer.billing.billingAddress.country
      },
      set (value) {
        this.$store.commit('customer/billingCountry', value)
      }
    },
    billingCustomerServiceAgreement: {
      get () {
        return this.$store.state.customer.billingCustomerServiceAgreement
      },
      set (value) {
        this.$store.commit('customer/billingCustomerServiceAgreement', value)
      }
    },
    billingSameAsDelivery: {
      get () {
        return this.$store.state.customer.billingSameAsDelivery
      },
      set (value) {
        this.$store.commit('customer/billingSameAsDelivery', value)
      }
    },
    billingMobilePhoneNumber: {
      get () {
        return this.$store.state.customer.billing.billingMobilePhoneNumber
      },
      set (value) {
        this.$store.commit('customer/billingMobilePhoneNumber', getFormattedPhoneNumber(value))
      }
    },
    consentToReceiveTexts: {
      get () {
        return this.$store.state.customer.consentToReceiveTexts
      },
      set (value) {
        this.$store.commit('customer/consentToReceiveTexts', value)
      }
    },
    discountCode: {
      get () {
        return this.$store.state.cart.discountCode
      },
      set (value) {
        this.$store.commit('cart/discountCode', value)
      }
    },
    serviceAddress () {
      return this.$store.state.customer.serviceAddress
    },
    deliveryDate () {
      return this.$store.state.customer.deliveryDate
    },
    endOfRentalPeriodDate () {
      const intRentalPeriod = parseInt(this.rentalPeriod)
      const endDate = addDays(this.deliveryDate, intRentalPeriod)
      return lightFormat(endDate, 'MM-dd-yyyy')
    },
    dumpsterSize () {
      return this.$store.state.cart.item.size
    },
    selectedDebrisType () {
      return this.$store.state.project.selectedDebrisType
    },
    tonnage () {
      return this.$store.state.cart.item.tonnage
    },
    tonnageOverage () {
      return this.$store.state.cart.item.tonnageOverage
    },
    rentalPeriod () {
      return getNumericValue(this.$store.state.cart.item.rentalPeriod)
    },
    rentalPeriodOverage () {
      return this.$store.state.cart.item.rentalPeriodOverage
    },
    ppcLeadUUID () {
      return this.$store.state.cart.ppcLeadUUID
    }
  }
}
</script>
